import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

function Section({ children, className = '', id = '' }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.section
      id={id}
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 36 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function CTAButton({ className = '' }) {
  return (
    <a
      href="https://sfl.communicatewithintelligence.com/guide"
      className={`inline-block bg-amber-700 hover:bg-amber-800 text-white font-sans font-semibold tracking-widest text-xs px-8 py-4 rounded transition-all duration-300 hover:shadow-lg ${className}`}
    >
      DOWNLOAD THE 7-PILLARS GUIDE
    </a>
  );
}

function BookCallButton({ className = '' }) {
  return (
    <a
      href="https://links.communicatewithintelligence.com/widget/booking/QqowSBHMI8U5xjvEM89r"
      className={`inline-block bg-amber-700 hover:bg-amber-800 text-white font-sans font-semibold tracking-widest text-xs px-8 py-4 rounded transition-all duration-300 hover:shadow-lg ${className}`}
    >
      BOOK MY CALL
    </a>
  );
}

function PhotoPlaceholder({ label = 'Your Photo Here', className = '', aspect = 'aspect-square' }) {
  return (
    <div className={`relative overflow-hidden rounded-lg bg-stone-200 ${aspect} ${className}`}>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-400">
        <svg className="w-12 h-12 mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
        </svg>
        <span className="text-xs font-sans tracking-wider uppercase">{label}</span>
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-stone-300 opacity-50" />
    </div>
  );
}

function CommunicationCard({ icon, title, description, delay = 0 }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.5, delay }}
      className="text-center p-6"
    >
      <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
        {icon}
      </div>
      <h4 className="font-sans font-semibold text-stone-800 tracking-wide text-sm uppercase mb-3">{title}</h4>
      <p className="text-stone-500 text-sm leading-relaxed">{description}</p>
    </motion.div>
  );
}

function TransformationColumn({ title, items, accent }) {
  return (
    <div className={`rounded-lg p-8 ${accent === 'before' ? 'bg-stone-100' : 'bg-amber-50 border border-amber-100'}`}>
      <p className={`font-sans font-semibold tracking-widest text-xs uppercase mb-6 ${accent === 'before' ? 'text-stone-400' : 'text-amber-700'}`}>
        {title}
      </p>
      <div className="space-y-4">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className={`mt-1 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${accent === 'before' ? 'bg-stone-300' : 'bg-amber-600'}`}>
              {accent === 'before' ? (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <p className={`text-sm leading-relaxed ${accent === 'before' ? 'text-stone-500' : 'text-stone-700'}`}>{item}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Testimonial({ quote, name, title }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-stone-100 overflow-hidden flex flex-col h-full">
      <div className="h-1 bg-amber-600" />
      <div className="p-8 flex flex-col justify-between flex-1">
        <div>
          <svg className="w-8 h-8 text-amber-200 mb-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
          </svg>
          <p className="text-stone-600 italic leading-relaxed mb-6">{quote}</p>
        </div>
        <div>
          <p className="font-semibold text-stone-800">{name}</p>
          <p className="text-stone-400 text-sm">{title}</p>
        </div>
      </div>
    </div>
  );
}

export default function CommunicateWithIntelligence() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-stone-800 font-serif">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'}`}>
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <img src="/cw-logo.png" alt="Communicate with Intelligence" className="h-20 w-auto" />
          <a href="https://sfl.communicatewithintelligence.com/guide" className="hidden sm:inline-block text-sm font-sans font-semibold text-amber-700 hover:text-amber-800 transition-colors">
            Download the 7-Pillars Guide →
          </a>
        </div>
      </nav>

      {/* Hero — Split Layout */}
      <section className="flex items-center bg-stone-50 px-6 pt-40 pb-36">
        <div className="max-w-5xl mx-auto w-full grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div className="order-2 md:order-1">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-amber-700 font-sans font-semibold tracking-widest text-xs mb-6 uppercase"
            >
              For High-Achieving Professionals Over 40
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-3xl md:text-4xl font-bold leading-tight text-stone-900 mb-6"
            >
              You built an impressive life.{' '}
              <span className="text-stone-500">
                So why doesn't it feel the way you thought it would?
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-lg text-stone-500 mb-8 leading-relaxed"
            >
              To learn more about key areas where the outer and inner life separate, read my free guide: <em className="text-stone-700">The 7-Pillars of an Authentically Fulfilled Life.</em>
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <CTAButton />
            </motion.div>
          </div>
          <motion.div
            className="order-1 md:order-2"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="max-w-sm mx-auto md:max-w-none overflow-hidden rounded-lg">
              <img
                src="/mary-headshot.png"
                alt="Mary Lazier Smith"
                className="w-full h-auto md:h-96 object-cover rounded-lg"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Atmospheric Image Band + Something Isn't Adding Up */}
      <Section id="problem" className="bg-white">
        {/* Atmospheric image placeholder */}
        <div className="relative w-full h-72 md:h-96 overflow-hidden">
          <img
            src="/boardroom-stare.png"
            alt="Boardroom stare"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-white opacity-70" />
        </div>
        <div className="max-w-2xl mx-auto px-6 py-20 md:py-28">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-10">
            Something Isn't Adding Up
          </h2>
          <div className="space-y-6 text-lg leading-relaxed text-stone-600">
            <p>
              You've spent decades doing the work. The career is impressive. The discipline is real.
              People around you see someone who's made it.
            </p>
            <p>
              But lately, there's a gap between what your life looks like and what it actually feels like.
              A quiet restlessness. A sense that something essential is missing — and you're not sure
              who you'd even talk to about it. It's not the kind of thing you bring up at dinner or
              in a boardroom.
            </p>
            <p>
              You've tried pushing through it. You've tried ignoring it. Maybe you've tried therapy,
              books, retreats. And yet here you are, still carrying it.
            </p>
            <p className="text-stone-800 font-medium">
              You're not broken. You're not ungrateful. You're just ready for something that actually works.
            </p>
          </div>
        </div>
      </Section>

      {/* The Real Problem — with 3 communication cards */}
      <Section className="py-20 md:py-28 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6">
              The Real Problem No One Talks About
            </h2>
            <p className="text-lg leading-relaxed text-stone-600 mb-12">
              The emptiness that successful people feel almost always traces back to a breakdown in
              three types of communication:
            </p>
          </div>

          {/* Three communication type cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-14">
            <CommunicationCard
              delay={0}
              icon={
                <svg className="w-7 h-7 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                </svg>
              }
              title="With Yourself"
              description="The inner conversation that shapes every decision, every feeling of worth, and every moment of self-doubt."
            />
            <CommunicationCard
              delay={0.15}
              icon={
                <svg className="w-7 h-7 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
              }
              title="With Others"
              description="The connection you share — or have quietly lost — with the people who matter most in your life."
            />
            <CommunicationCard
              delay={0.3}
              icon={
                <svg className="w-7 h-7 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                </svg>
              }
              title="With Something Greater"
              description="Your relationship to meaning, purpose, and something beyond the daily grind that makes it all worthwhile."
            />
          </div>

          <div className="max-w-2xl space-y-6 text-lg leading-relaxed text-stone-600">
            <p>
              When those three channels are disconnected — and for most high-performers, they've been
              running on static for years — no amount of achievement will ever feel like enough. Not the
              next promotion. Not the next milestone. Not the next vacation.
            </p>
            <p className="text-stone-800 font-medium">
              This isn't a willpower problem. It's not a gratitude problem. It's a communication
              problem — just not the kind anyone ever taught you to see.
            </p>
          </div>
        </div>
      </Section>

      {/* What CWI Actually Is — with transformation columns */}
      <Section id="system" className="py-20 md:py-28 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="max-w-2xl mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6">
              What Communicate with Intelligence™ Actually Is
            </h2>
            <div className="space-y-6 text-lg leading-relaxed text-stone-600">
              <p>
                A transformation process that uses the one skill professionals already
                rely on every day — communication — as the vehicle for genuine, lasting change.
              </p>
              <p>
                It works across three interrelated, interdependent loops that determine the quality of your life:
                how you communicate with yourself, how you show up in your closest relationships,
                and how you connect to meaning and purpose beyond your career.
              </p>
            </div>
          </div>

          {/* Before / After Columns */}
          <div className="grid md:grid-cols-2 gap-6 mb-14">
            <TransformationColumn
              title="Where You Are Now"
              accent="before"
              items={[
                'Restless at 3am with no clear reason why',
                'Going through the motions in your closest relationships',
                'Achieving more but feeling less',
                'A growing sense that something essential is missing',
                'Running on discipline and willpower — and running out of both',
              ]}
            />
            <TransformationColumn
              title="Where This Takes You"
              accent="after"
              items={[
                'Genuine peace that doesn\'t depend on the next win',
                'Relationships that feel intimate and deeply connected again',
                'Professional performance that sharpens — not from pressure, but from clarity',
                'A sense of purpose that\'s yours, not borrowed from a job title',
                'Energy and fulfillment that come from the inside out',
              ]}
            />
          </div>

          {/* Video */}
          <div className="max-w-2xl mx-auto mb-14">
            <div className="relative w-full aspect-video">
              <iframe
                className="absolute inset-0 w-full h-full rounded-lg"
                src="https://www.youtube.com/embed/Y23jVq6wgvg?si=8RnTW_ggJwpHplCS"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>

          <div className="max-w-2xl">
            <p className="text-lg leading-relaxed text-stone-800 font-medium mb-6">
              This is not motivational content. It is not abstract philosophy. It is a practical,
              rigorous process built on over 30 years of research and application — and it produces
              real, lasting change, not the temporary kind that fades when life gets busy again.
            </p>
            <p className="text-lg leading-relaxed text-stone-600 mb-10">
              Ready to see what this transformation journey might look like for you? Book your 40-minute complimentary call with me.
            </p>
            <div className="flex flex-wrap gap-4">
              <BookCallButton />
              <CTAButton />
            </div>
          </div>
        </div>
      </Section>

      {/* Divider Quote */}
      <Section className="relative py-20 md:py-28 px-6 bg-stone-900 text-white overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute top-8 left-8 w-40 h-40 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
          </svg>
          <svg className="absolute bottom-8 right-8 w-40 h-40 text-amber-500 rotate-180" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
          </svg>
        </div>
        {/* Subtle line accents */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-600 to-transparent opacity-40" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-600 to-transparent opacity-40" />

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <p className="text-2xl md:text-3xl font-light leading-relaxed italic text-stone-300">
            "Unintelligent communication creates division, distrust, and disconnection.
          </p>
          <p className="text-2xl md:text-3xl font-light leading-relaxed italic text-stone-300 mt-4">
            Intelligent communication creates connection, trust, and a life that finally feels like yours."
          </p>
          <div className="mt-8 w-16 h-px bg-amber-600 mx-auto" />
          <p className="mt-4 text-amber-500 font-sans text-sm tracking-widest uppercase">Mary Lazier Smith</p>
        </div>
      </Section>

      {/* Who I Am */}
      <Section id="about" className="py-20 md:py-28 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          {/* Intro + Photo */}
          <div className="grid md:grid-cols-5 gap-10 md:gap-14 items-start">
            <div className="md:col-span-2">
              <img
                src="/coaching.jpeg"
                alt="Mary Lazier Smith speaking"
                className="w-full h-72 md:h-96 object-cover rounded-lg"
              />
              <div className="mt-4 flex flex-wrap gap-1.5">
                {['BA, Communication', 'MA, Communication', 'Integral Theory', 'EQ Certified', 'SQ Certified'].map(
                  (cred) => (
                    <span
                      key={cred}
                      className="text-[11px] font-sans bg-stone-100 text-stone-500 px-2.5 py-1 rounded-full"
                    >
                      {cred}
                    </span>
                  )
                )}
              </div>
            </div>
            <div className="md:col-span-3 space-y-4 text-lg leading-relaxed text-stone-600">
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Who I Am</h2>
              <p>For more than three decades, I've studied one question:</p>
              <p className="text-stone-800 font-medium italic">
                Why do so many intelligent, capable, successful people feel disconnected — from themselves, from the people they love, and from the life they've built?
              </p>
              <p>
                I began my career in communication studies, earning both a BA and MA in Communication. I taught at the college level, trained corporate teams, and facilitated programs in leadership, emotional intelligence, culture building, and personal development.
              </p>
              <p className="text-stone-700">On paper, everything made sense.</p>
            </div>
          </div>

          {/* Insight */}
          <div className="max-w-2xl mx-auto mt-10 space-y-4 text-lg leading-relaxed text-stone-600">
            <p className="text-stone-800 font-medium">But in real life, I kept seeing something deeper.</p>
            <p>
              Highly accomplished professionals who could lead companies — but couldn't connect at home.
              Brilliant thinkers who could solve complex problems — but felt quietly empty inside.
              Families who loved each other — but didn't know how to communicate without defensiveness or distance.
            </p>
            <p>
              The common thread wasn't intelligence. It wasn't effort. And it certainly wasn't lack of success.
            </p>
            <p className="text-stone-800 font-medium">It was communication.</p>
            <p>
              Not surface-level skills. Not presentation technique. But the deeper structures:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>The internal dialogue shaping identity and pressure</li>
              <li>The relational patterns driving trust or distrust</li>
              <li>The way people relate to meaning, purpose, and something greater than achievement</li>
            </ul>
          </div>

          {/* Discovery + Method */}
          <div className="max-w-2xl mx-auto mt-8 space-y-4 text-lg leading-relaxed text-stone-600">
            <p>
              Over 30+ years, I studied and integrated insights from communication science, emotional intelligence, leadership development, developmental psychology, integral theory, somatic awareness, and contemplative traditions — influenced by thinkers such as Stephen R. Covey, Byron Katie, Ken Wilber, and many others.
            </p>
            <blockquote className="py-4 px-5 border-l-4 border-amber-600 bg-amber-50/50 rounded-r-lg">
              <p className="text-stone-800 font-medium">
                Nothing changes without communication. And communication is always happening.
              </p>
            </blockquote>
            <p>
              That realization became the foundation for the Communicate with Intelligence™ System — a step-by-step human development process that uses communication as the lever for transformation.
            </p>
            <p>
              My work is not about abandoning ambition. It's not about rejecting success. And it's not about becoming someone different.
            </p>
            <p className="text-stone-800 font-semibold text-xl text-center">It's about alignment.</p>
            <p className="text-center text-stone-500">
              The emptiness lifts. Relationships soften. Leadership deepens. Legacy becomes intentional.
            </p>
          </div>

          {/* Personal closing */}
          <div className="max-w-2xl mx-auto mt-8 text-center space-y-3 text-lg leading-relaxed text-stone-600">
            <p className="text-stone-800 font-medium italic">
              I understand what it means for your life to look right and feel wrong.
            </p>
            <p className="text-stone-700">
              And I know what becomes possible when that gap finally closes.
            </p>
            <p>
              If you're ready to rebuild connection — from the inside out — I'd be honored to guide the process.
            </p>
            <div className="pt-4 flex flex-wrap justify-center gap-4">
              <BookCallButton />
              <CTAButton />
            </div>
          </div>
        </div>
      </Section>

      {/* Testimonials */}
      <Section id="testimonials" className="py-20 md:py-28 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-3 text-center">
            What Others Are Saying
          </h2>
          <p className="text-stone-400 text-center mb-14 text-lg">
            From professionals who've done the work
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <Testimonial
              quote="Mary taught me how to acknowledge my perceptions and triggers helping me approach uncomfortable situations and conversations positively, which, in turn, helped me in my career and my personal life."
              name="Carolyn M."
              title="Customer Service Director"
            />
            <Testimonial
              quote="Mary's teaching and coaching have had a profound impact on my life. She has guided me to new levels of self-awareness and connection... improved my communication skills and empowered me to express myself."
              name="Will W."
              title="Customer Service Manager"
            />
            <Testimonial
              quote="Mary is a fantastic coach with beautiful energy. Her coaching has helped me approach and engage with the world on a higher level, allowing me to find more joy in my life."
              name="Arch A."
              title="Supply Chain Manager"
            />
            <Testimonial
              quote="My relationship with myself, my children, and others has exponentially improved, and I, for the first time in a long time, feel positive about life and look forward to my contribution."
              name="Jaylyn F."
              title="Daughter & Parent"
            />
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="relative py-24 md:py-32 px-6 overflow-hidden bg-amber-50">
        {/* Warm radial glow */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-200 rounded-full opacity-30 blur-3xl" />
        </div>
        <div className="max-w-2xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
            You've begun your research.
          </h2>
          <p className="text-xl text-stone-600 mb-10">Now take the next step.</p>
          <BookCallButton />
          <p className="text-stone-400 text-sm mt-8 font-sans">
            Free. No obligation. Just clarity.
          </p>
        </div>
      </Section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-stone-900 text-stone-400">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-sm">
          <div className="text-center md:text-left">
            <p className="text-stone-300 font-sans font-semibold tracking-wide mb-1">
              COMMUNICATE WITH INTELLIGENCE™
            </p>
            <p>Soul-Full-Living LLC</p>
          </div>
          <div className="text-center md:text-right space-y-1">
            <p>
              <a href="mailto:mary@soul-full-living.com" className="hover:text-white transition-colors">
                mary@soul-full-living.com
              </a>
            </p>
            <p>855-499-0279</p>
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-8 pt-6 border-t border-stone-800 text-center text-xs text-stone-500">
          © {new Date().getFullYear()} Communicate with Intelligence — All Rights Reserved. &nbsp;
          <Link to="/privacy" className="hover:text-stone-300 transition-colors">Privacy Policy</Link>{' '}
          |{' '}
          <Link to="/terms" className="hover:text-stone-300 transition-colors">Terms and Conditions</Link>
        </div>
      </footer>
    </div>
  );
}
